import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, Paperclip, X, FileText } from 'lucide-react';
import {
    type ChatUploadConfig,
    DEFAULT_CHAT_UPLOAD_CONFIG,
    CHAT_UPLOAD_ACCEPT,
    formatFileSize,
    isSupportedUploadFile,
    loadChatUploadConfig,
} from '../chat/chatUploadConfig';

// Visual parity with HomeChatAgent's composer: same container, typography,
// send button. Behaviourally different — submit here creates a project and
// the parent navigates to the workspace. No conversation persists on this page.

const RESEARCH_EXAMPLES = [
    'What are the CRR capital requirements for corporate exposures?',
    'Summarise MiFID II position reporting obligations',
    'When does PSD2 strong customer authentication apply?',
    'What does EMIR say about clearing thresholds for pension schemes?',
    'How is countercyclical buffer calibrated across Nordic jurisdictions?',
];

const BUILD_EXAMPLES = [
    'Build an NMD behavioural model for LCR outflow categorisation',
    'Build an ECL model with IFRS 9 stage classification',
    'Scope an ICAAP capital plan for Swedish regulatory reporting',
    'Draft a Pillar 3 disclosure for a Nordic bank',
    'Help me build an IRRBB sensitivity model with NII scenarios',
];

interface ProjectSeedComposerProps {
    projectType: 'research' | 'build';
    seedText?: string;
    onSeedConsumed?: () => void;
    // BAR-523: files attached at project start. Empty array when none.
    onSubmit: (message: string, files: File[]) => void;
    isSubmitting?: boolean;
}

export default function ProjectSeedComposer({
    projectType,
    seedText = '',
    onSeedConsumed,
    onSubmit,
    isSubmitting = false,
}: ProjectSeedComposerProps) {
    const [input, setInput] = useState('');
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const [placeholderText, setPlaceholderText] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // BAR-523: attach files at project start (uploaded after the project is
    // created — see NewProjectForm.submitProject).
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadConfig, setUploadConfig] = useState<ChatUploadConfig>(DEFAULT_CHAT_UPLOAD_CONFIG);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let cancelled = false;
        loadChatUploadConfig().then((config) => {
            if (!cancelled) setUploadConfig(config);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleFileSelection = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setUploadError(null);
        const accepted: File[] = [];
        const isDuplicate = (f: File) =>
            [...pendingFiles, ...accepted].some(
                (o) => o.name === f.name && o.size === f.size && o.lastModified === f.lastModified,
            );
        for (const file of Array.from(files)) {
            if (isDuplicate(file)) continue; // already attached — silently skip
            if (pendingFiles.length + accepted.length >= uploadConfig.maxFilesPerWorkspace) {
                setUploadError(`You can attach up to ${uploadConfig.maxFilesPerWorkspace} files.`);
                break;
            }
            if (file.size > uploadConfig.maxBytes) {
                setUploadError(`${file.name} is larger than ${formatFileSize(uploadConfig.maxBytes)}.`);
                continue;
            }
            if (!isSupportedUploadFile(file, uploadConfig)) {
                setUploadError(`${file.name} is not a supported file type.`);
                continue;
            }
            accepted.push(file);
        }
        if (accepted.length > 0) {
            setPendingFiles((current) => [...current, ...accepted]);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removePendingFile = (index: number) => {
        setPendingFiles((current) => current.filter((_, i) => i !== index));
    };

    const examples = projectType === 'build' ? BUILD_EXAMPLES : RESEARCH_EXAMPLES;

    // Apply external seed (from chip clicks in the showcase). The parent
    // clears `seedText` to '' via `onSeedConsumed` immediately after we
    // apply, so the state always cycles '' -> seed -> ''. That means a
    // same-chip re-click produces a new '' -> seed transition and fires
    // this effect again — no need for a "did I already apply this seed"
    // comparison against `input`, which was stale-closure-prone.
    useEffect(() => {
        if (!seedText) return;
        setInput(seedText);
        inputRef.current?.focus();
        onSeedConsumed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seedText]);

    // Typewriter placeholder cycle (mirrors HomeChatAgent). Both the typing
    // interval AND the 2-second pause setTimeout are tracked so the cleanup
    // can cancel both: unmount during the pause previously leaked a state
    // update on the unmounted component.
    useEffect(() => {
        if (input.length > 0) return;
        const currentQ = examples[placeholderIndex];
        let interval: ReturnType<typeof setInterval> | undefined;
        let pauseTimeout: ReturnType<typeof setTimeout> | undefined;

        if (isTyping) {
            let charIndex = 0;
            interval = setInterval(() => {
                charIndex++;
                setPlaceholderText(currentQ.slice(0, charIndex));
                if (charIndex >= currentQ.length) {
                    if (interval) clearInterval(interval);
                    pauseTimeout = setTimeout(() => setIsTyping(false), 2000);
                }
            }, 35);
        } else {
            setPlaceholderText('');
            setPlaceholderIndex((i) => (i + 1) % examples.length);
            setIsTyping(true);
        }
        return () => {
            if (interval) clearInterval(interval);
            if (pauseTimeout) clearTimeout(pauseTimeout);
        };
    }, [placeholderIndex, isTyping, input, examples]);

    const handleSend = () => {
        if ((!input.trim() && pendingFiles.length === 0) || isSubmitting) return;
        setUploadError(null);
        onSubmit(input.trim(), pendingFiles);
        // Do NOT clear pendingFiles here: on success the composer unmounts
        // (navigation into the new project), and on a create/upload failure the
        // files must survive so the user can retry without re-selecting them.
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const fallback = projectType === 'build'
        ? 'Describe what you want to build…'
        : 'Ask a regulatory question…';
    const placeholder = input.length > 0 ? '' : (placeholderText || fallback);

    return (
        <div
            className={`relative bg-surface border border-border-strong rounded-xl shadow-bark-xs transition-shadow cursor-text ${
                projectType === 'build'
                    ? 'focus-within:border-accent-copper focus-within:shadow-bark-focus-build'
                    : 'focus-within:border-accent focus-within:shadow-bark-focus'
            }`}
            onClick={() => inputRef.current?.focus()}
        >
            {(pendingFiles.length > 0 || uploadError) && (
                <div className="px-4 pt-3 flex flex-wrap gap-1.5">
                    {pendingFiles.map((file, index) => (
                        <div
                            key={`${file.name}-${file.size}-${index}`}
                            className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded border border-border-default bg-bg-warm text-[11px] text-fg-secondary"
                            title={`${file.name} (${formatFileSize(file.size)})`}
                        >
                            <FileText size={12} className="text-fg-tertiary flex-shrink-0" />
                            <span className="truncate max-w-[220px]">{file.name}</span>
                            <span className="text-fg-tertiary flex-shrink-0">{formatFileSize(file.size)}</span>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); removePendingFile(index); }}
                                disabled={isSubmitting}
                                className="p-0.5 rounded hover:bg-border-default text-fg-tertiary disabled:opacity-50"
                                title="Remove file"
                                aria-label={`Remove ${file.name}`}
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                    {uploadError && (
                        <span className="basis-full text-[11px] text-danger">{uploadError}</span>
                    )}
                </div>
            )}
            <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                rows={1}
                disabled={isSubmitting}
                className="w-full font-body text-[17px] leading-[1.5] text-fg placeholder-fg-tertiary bg-transparent border-none outline-none resize-none px-5 pt-5 pb-12 disabled:opacity-60 disabled:cursor-wait focus-visible:shadow-none"
                style={{ minHeight: 82 }}
                autoFocus
            />
            <div className="absolute bottom-3 right-3 flex items-center gap-1">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept={CHAT_UPLOAD_ACCEPT}
                    onChange={(e) => handleFileSelection(e.target.files)}
                />
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    disabled={isSubmitting}
                    className="w-[30px] h-[30px] flex items-center justify-center rounded-md text-fg-tertiary hover:text-fg-secondary hover:bg-bg-warm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Attach file"
                    aria-label="Attach file"
                >
                    <Paperclip className="w-4 h-4" strokeWidth={1.8} />
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleSend(); }}
                    disabled={(!input.trim() && pendingFiles.length === 0) || isSubmitting}
                    className="w-[30px] h-[30px] flex items-center justify-center bg-primary text-on-primary rounded-md hover:bg-primary-hover disabled:bg-border-strong disabled:cursor-not-allowed transition-colors"
                    aria-label={isSubmitting ? 'Creating project…' : 'Send'}
                >
                    <ArrowUp className="w-4 h-4" strokeWidth={2} />
                </button>
            </div>
        </div>
    );
}
